import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
interface HeroProps {
  onSearch?: (service: string, zip: string) => void;
}
export function Hero({
  onSearch
}: HeroProps) {
  const [service, setService] = useState('');
  const [zip, setZip] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && service && zip) {
      onSearch(service, zip);
    } else if (!service) {
      alert('Please select a service type');
    } else if (!zip) {
      alert('Please enter your zip code');
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-id="element-1553">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-1554">
        <img src="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=2000&q=80" alt="Contractor working on home improvement" className="w-full h-full object-cover" data-id="element-1555" />
        <div className="absolute inset-0 bg-slate-900/80" data-id="element-1556" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1557">
        <motion.div className="text-center max-w-4xl mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-1558">
          <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-1559">
            Find Trusted Local Pros.
            <br className="hidden md:block" data-id="element-1560" /> Compare Free Quotes.
          </motion.h1>

          <motion.p variants={itemVariants} className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto" data-id="element-1561">
            Connect with vetted service professionals in your area. Get multiple
            quotes, compare prices, and hire with confidence.
          </motion.p>

          <motion.div variants={itemVariants} className="max-w-3xl mx-auto bg-white rounded-2xl p-2 md:p-3 shadow-xl" data-id="element-1562">
            <form className="flex flex-col md:flex-row gap-3" onSubmit={handleSubmit} data-id="element-1563">
              <div className="flex-1 relative" data-id="element-1564">
                <label htmlFor="service-type" className="sr-only" data-id="element-1565">
                  Select Service
                </label>
                <select id="service-type" value={service} onChange={e => setService(e.target.value)} className="w-full h-14 md:h-16 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent appearance-none" data-id="element-1566">
                  <option value="" disabled data-id="element-1567">
                    What do you need help with?
                  </option>
                  <optgroup label="Bathroom" data-id="element-1568">
                    <option value="walk-in-tubs" data-id="element-1569">Walk-In Tubs</option>
                    <option value="bathroom-remodel" data-id="element-1570">Bathroom Remodel</option>
                  </optgroup>
                  <optgroup label="Exterior" data-id="element-1571">
                    <option value="roofing" data-id="element-1572">Roofing</option>
                    <option value="siding" data-id="element-1573">Siding</option>
                    <option value="window-replacement" data-id="element-1574">
                      Window Replacement
                    </option>
                    <option value="gutter-guards" data-id="element-1575">Gutter Guards</option>
                  </optgroup>
                  <optgroup label="Systems" data-id="element-1576">
                    <option value="hvac" data-id="element-1577">HVAC</option>
                    <option value="home-security" data-id="element-1578">Home Security</option>
                  </optgroup>
                  <optgroup label="Interior" data-id="element-1579">
                    <option value="flooring" data-id="element-1580">Flooring</option>
                  </optgroup>
                </select>
              </div>

              <div className="flex-1 relative" data-id="element-1581">
                <label htmlFor="zip-code" className="sr-only" data-id="element-1582">
                  Zip Code
                </label>
                <input type="text" id="zip-code" value={zip} onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="Enter your zip code" maxLength={5} className="w-full h-14 md:h-16 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-1583" />
              </div>

              <button type="submit" className="h-14 md:h-16 px-8 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-white" data-id="element-1584">
                Get Free Quotes
                <ArrowRightIcon className="w-5 h-5" data-id="element-1585" />
              </button>
            </form>
          </motion.div>

          <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center gap-2 text-slate-300 text-sm font-medium" data-id="element-1586">
            <ShieldCheckIcon className="w-5 h-5 text-amber-500" data-id="element-1587" />
            <span data-id="element-1588">Trusted by 50,000+ homeowners across the country</span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}